import Button from '@ixigo/iui/Button';
import Typography from '@ixigo/iui/Typography';
import cn from '@repo/shared/utils/cn';
import { formatPrice } from '@repo/shared/utils/priceFormatter';
import { useState } from 'react';
import type { BaseFareSummary } from '@/api';
import { formatDateTimeLabel } from '@/components/date-time-picker';
import type {
	RescheduleCabOption,
	RescheduleFareData,
} from '../../../-api/reschedule';
import { tripTracker } from '../../../-utils/tracker';
import RedeemIxigoMoney from './redeem-ixigo-money';

interface PaymentBreakdownProps {
	selectedCab: RescheduleCabOption;
	fareData: RescheduleFareData;
	updatedDateTime: string;
	onAction: (burnOpted?: boolean) => void;
}

export const PaymentBreakdown = (props: PaymentBreakdownProps) => {
	const { selectedCab, fareData, updatedDateTime, onAction } = props;
	const { fareNote } = fareData;
	const { currency, burn, strikePrice } = selectedCab.pricing;
	const { differentialAmount, displayFares, message, iconUrl, status } =
		selectedCab.rescheduleDetails;
	const formatOpts = { currency };

	const [burnOpted, setBurnOpted] = useState(
		status === 'PAYMENT_REQUIRED' && burn > 0,
	);
	const amountDue = burnOpted ? differentialAmount - burn : differentialAmount;

	const handleActionClick = () => {
		tripTracker.tracker.initPayment({
			selectedDateTime: updatedDateTime,
			selectedCab,
			burnOpted,
		});
		onAction(burnOpted);
	};

	return (
		<div className="flex flex-col">
			<div className="flex flex-col items-center px-20 pt-30 pb-20 gap-y-20 bg-gradient-to-b from-warning-moderate to-primary rounded-t-30">
				<div className="flex flex-col items-center gap-y-15">
					<img src={iconUrl} alt="" height={70} width={70} />
					<div className="flex flex-col items-center">
						<Typography
							variant="h4"
							fontWeight="bold"
							className={status === 'REFUND_ELIGIBLE' ? 'text-success' : ''}
						>
							{formatPrice(amountDue, formatOpts)}
						</Typography>
						<Typography variant="body-md">{message}</Typography>
					</div>
				</div>

				<div className="flex flex-col gap-y-5 w-full">
					<div className="rounded-20 border border-tertiary bg-primary w-full p-15 space-y-5">
						{displayFares.map((fare, index) => (
							<FareDetailsCard
								key={fare.label}
								fare={fare}
								formatOpts={formatOpts}
								strikePrice={strikePrice}
								className={
									index < displayFares.length - 1
										? 'border-b border-tertiary pb-10'
										: ''
								}
							/>
						))}
					</div>

					{fareNote && (
						<Typography
							variant="body-xs"
							className="text-secondary text-left w-full"
						>
							Note: {fareNote}
						</Typography>
					)}
					{status === 'PAYMENT_REQUIRED' && burn > 0 ? (
						<RedeemIxigoMoney
							amount={burn}
							checked={burnOpted}
							className="mt-2.5"
							onValueChange={(e) => setBurnOpted(e.target.checked)}
						/>
					) : null}
				</div>
			</div>
			<div className="w-full px-20 py-10 space-y-10 sticky bottom-0 inset-x-0 border-t border-tertiary">
				<Typography
					variant="body-sm"
					className="text-primary w-full text-center"
				>
					Updated Time:{' '}
					<Typography as="span" variant="body-sm" fontWeight="bold">
						{formatDateTimeLabel(updatedDateTime)}
					</Typography>
				</Typography>
				<Button
					variant="solid"
					color="brand"
					size="lg"
					className="w-full"
					onClick={handleActionClick}
				>
					{status === 'PAYMENT_REQUIRED' && amountDue > 0
						? `Pay ${formatPrice(amountDue, formatOpts)}`
						: 'Reschedule Now'}
				</Button>
			</div>
		</div>
	);
};

export const FareDetailsCard = (props: {
	fare: BaseFareSummary;
	formatOpts: {
		currency: string | undefined;
	};
	strikePrice?: number;
	className?: string;
}) => {
	const { fare, formatOpts, strikePrice, className } = props;

	return (
		<div
			className={cn('flex justify-between items-center py-5 gap-20', className)}
		>
			<Typography variant="body-md">{fare.label}</Typography>
			<div className="flex items-baseline justify-end">
				{strikePrice && fare.label === 'Current Fare' ? (
					<Typography
						variant="body-xs"
						className="text-critical line-through decoration-current inline-block align-baseline pr-0.5"
					>
						<span className="text-secondary">
							{formatPrice(strikePrice, formatOpts)}
						</span>
					</Typography>
				) : null}
				<Typography variant="body-md">
					{formatPrice(fare.amount ?? 0, formatOpts)}
				</Typography>
			</div>
		</div>
	);
};
